import { useEffect, useMemo, useRef, useState } from "react";
import config from "../../../../../config/config";
import { apiCall } from "../../../../../_services/apiCall";
import { buildFinancialYearRange } from "../utility/quickViewPayloadBuilder";
import { buildQuickViewMap } from "../utility/quickViewMapper";

/** Period columns are empty until the first response lands. */
const EMPTY_BUCKETS = { MONTHLY: [], QUARTERLY: [], HALFYEARLY: [], YEARLY: [] };

/**
 * Fetches every period column's values for one financial year in a single
 * request. The endpoint returns monthly, quarterly, half-yearly and yearly
 * buckets together, so changing the period filter needs no further call. The
 * alert popup is suppressed (6th arg false) because the page renders its own
 * inline failure banner with a retry.
 *
 * @param {number} financialYearId - Selected financial year id.
 * @param {{fromDate: string, toDate: string}} range - Inclusive FY date range.
 * @param {number} startingMonth - Fiscal year start month (1-12).
 * @returns {Promise<Object>} The raw apiCall result.
 */
const fetchPeriodAnalytics = (financialYearId, range, startingMonth) =>
  apiCall(
    `${config.POSTLOGIN_API_URL_COMPANY}quantative-data/period-analytics`,
    {},
    {
      financialYearId,
      fromDate: range.fromDate,
      toDate: range.toDate,
      startingMonth,
    },
    "GET",
    true,
    false
  );

/**
 * Owns Quick View's data. Questions and answers are fetched concurrently - the
 * answers call depends only on the financial year and starting month, not on
 * the question list - so the table paints as soon as questions arrive while
 * values are still in flight.
 *
 * Every filter is client-side: location, sub-location and module narrow the
 * stored rows, and the period selector just reads a different set of bucket
 * keys out of the single response. Only changing the financial year re-queries.
 *
 * @param {Object} params
 * @param {?number} params.financialYearId - Selected FY id; fetch waits for it.
 * @param {?string} params.financialYearValue - Selected FY label, e.g. "2026-2027".
 * @param {string[]} params.selectedLocationIds - Selected location values.
 * @param {string[]} params.selectedSubLocationIds - Selected sub-location values.
 * @param {Array<{value: string, label: string, parentValue: string}>} params.subLocations
 *   - All sub-location options, passed through to buildQuickViewMap.
 * @param {number[]} params.selectedModuleIds - Selected reporting-UI module ids.
 * @param {"MONTHLY"|"QUARTERLY"|"HALFYEARLY"|"YEARLY"} params.periodAggregation
 * @param {Array<{value: string, label: string}>} params.locations - All location options.
 * @returns {{questions: Array<Object>, filteredQuestions: Array<Object>,
 *   quickViewMap: Object, rawRows: Array<Object>, unitSettingsMap: Object,
 *   startingMonth: ?number, periods: Array<Object>, loadingQuestions: boolean,
 *   loadingAnswers: boolean, answersFailed: boolean, retrying: boolean,
 *   retryAnswers: () => Promise<void>}} Data, loading flags, and the failure
 *   state plus a retry for the answers call.
 */
export const useQuickView = ({
  financialYearId,
  financialYearValue,
  selectedLocationIds,
  selectedSubLocationIds,
  subLocations,
  selectedModuleIds,
  periodAggregation,
  locations,
}) => {
  const [questions, setQuestions] = useState([]);
  const [rawRows, setRawRows] = useState([]);
  const [buckets, setBuckets] = useState(EMPTY_BUCKETS);
  const [unitSettingsMap, setUnitSettingsMap] = useState({});
  const [startingMonth, setStartingMonth] = useState(null);
  const [answersFailed, setAnswersFailed] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [loadingQuestions, setLoadingQuestions] = useState(false);
  const [loadingAnswers, setLoadingAnswers] = useState(false);
  const isMounted = useRef(true);
  // Everything needed to re-issue the answers call on retry, captured at fetch
  // time so a retry can never drift from the request that failed.
  const answersRequestRef = useRef(null);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (!financialYearId || !financialYearValue) return;
    let cancelled = false;

    /**
     * Loads one financial year. The starting month, unit settings and framework
     * list resolve together first; questions and answers then run concurrently,
     * since the answers request needs only the year and starting month. Starting
     * month falls back to April (4) when its call fails or returns nothing
     * usable.
     *
     * @returns {Promise<void>}
     */
    const loadAll = async () => {
      setLoadingQuestions(true);
      setLoadingAnswers(true);
      setRawRows([]);
      setBuckets(EMPTY_BUCKETS);
      setAnswersFailed(false);

      try {
        const [frameworkResponse, unitRes, monthRes] = await Promise.all([
          apiCall(`${config.POSTLOGIN_API_URL_COMPANY}getFramework`, {}, { type: "ALL" }),
          apiCall(
            `${config.POSTLOGIN_API_URL_COMPANY}getUnitKpiSettings`,
            {},
            { fyid: financialYearId },
            "GET"
          ),
          apiCall(`${config.POSTLOGIN_API_URL_COMPANY}starting-month`, {}, {}, "GET").catch((e) => {
            console.warn("Failed to fetch starting month", e);
            return { isSuccess: false };
          }),
        ]);

        if (cancelled || !isMounted.current) return;

        let sm = 4;
        if (monthRes.isSuccess && monthRes.data?.data?.startingMonth) {
          const m = parseInt(monthRes.data.data.startingMonth, 10);
          if (m >= 1 && m <= 12) sm = m;
        }
        setStartingMonth(sm);

        if (unitRes.isSuccess) {
          const map = {};
          (unitRes.data?.data || []).forEach((item) => {
            if (item.kpiId != null && item.selectedUnit) {
              map[item.kpiId] = item.selectedUnit;
            }
          });
          setUnitSettingsMap(map);
        }

        const frameworkIds = frameworkResponse.isSuccess
          ? (frameworkResponse.data?.data || []).map((item) => item.id)
          : [];

        const range = buildFinancialYearRange(financialYearValue, sm);
        answersRequestRef.current = range
          ? { financialYearId, range, startingMonth: sm }
          : null;

        // Questions and answers are independent, so they fly together: the table
        // can paint from questions alone while values are still arriving.
        const [questionResponse, answersResponse] = await Promise.all([
          apiCall(
            `${config.POSTLOGIN_API_URL_COMPANY}getReportingQuestionnew`,
            {},
            { financialYearId, frameworkIds },
            "GET"
          ),
          range
            ? fetchPeriodAnalytics(financialYearId, range, sm)
            : Promise.resolve({ isSuccess: false }),
        ]);

        if (cancelled || !isMounted.current) return;

        let questionsArray = [];
        if (questionResponse.isSuccess) {
          questionsArray = Object.values(questionResponse.data?.data || {});
          questionsArray.sort((a, b) => {
            if (a.serialNumber == null) return 1;
            if (b.serialNumber == null) return -1;
            return a.serialNumber - b.serialNumber;
          });
        }
        setQuestions(questionsArray);
        setLoadingQuestions(false);

        if (answersResponse.isSuccess) {
          const payload = answersResponse.data?.data ?? answersResponse.data ?? {};
          setRawRows(payload.rows || []);
          setBuckets(payload.buckets || EMPTY_BUCKETS);
          setAnswersFailed(false);
        } else {
          setAnswersFailed(true);
        }
      } catch (error) {
        console.error("Error loading quick view data:", error);
        if (!cancelled && isMounted.current) {
          setQuestions([]);
          setRawRows([]);
          setAnswersFailed(true);
        }
      } finally {
        if (!cancelled && isMounted.current) {
          setLoadingQuestions(false);
          setLoadingAnswers(false);
        }
      }
    };

    loadAll();
    return () => {
      cancelled = true;
    };
  }, [financialYearId, financialYearValue]);

  /**
   * Re-issues the answers request after a failure, reusing the exact parameters
   * the original call was built with. Bails if the component unmounted or the
   * financial year moved on while the retry was in flight, so a late response
   * can never overwrite a different year's data.
   *
   * @returns {Promise<void>}
   */
  const retryAnswers = async () => {
    const request = answersRequestRef.current;
    if (!request || retrying) return;

    setRetrying(true);
    const response = await fetchPeriodAnalytics(
      request.financialYearId,
      request.range,
      request.startingMonth
    );

    if (!isMounted.current) return;
    if (answersRequestRef.current !== request) {
      setRetrying(false);
      return;
    }

    if (response.isSuccess) {
      const payload = response.data?.data ?? response.data ?? {};
      setRawRows(payload.rows || []);
      setBuckets(payload.buckets || EMPTY_BUCKETS);
      setAnswersFailed(false);
    } else {
      setAnswersFailed(true);
    }
    setRetrying(false);
  };

  const periods = useMemo(
    () => buckets?.[periodAggregation] || [],
    [buckets, periodAggregation]
  );

  const filteredQuestions = useMemo(
    () => questions.filter((q) => (selectedModuleIds || []).includes(q.moduleId)),
    [questions, selectedModuleIds]
  );

  const quickViewMap = useMemo(
    () =>
      buildQuickViewMap({
        rows: rawRows,
        questions: filteredQuestions,
        selectedLocationIds,
        locations,
        selectedSubLocationIds,
        subLocations,
      }),
    [rawRows, filteredQuestions, selectedLocationIds, locations, selectedSubLocationIds, subLocations]
  );

  return {
    questions,
    filteredQuestions,
    quickViewMap,
    rawRows,
    unitSettingsMap,
    startingMonth,
    periods,
    loadingQuestions,
    loadingAnswers,
    answersFailed,
    retrying,
    retryAnswers,
  };
};
